// Sim events → VFX (docs/ART-DIRECTION.md §7). EVENT_BINDINGS is the table: per event kind, which effects play and
// where (the placement each binding derives from the event, the setup map and the last known positions).
// EVENT_BOOKKEEPING runs after an event's effects: it updates what the layer follows (units, flight salvos).

import { STRIKE_KEYS, STRIKES, keyOf, type StrikeEffect, type StrikeKey } from '@taxwar/content'
import {
  InvariantViolation,
  NEUTRAL_OWNER,
  NO_SIM_ID,
  SIM_HZ,
  type CityIndex,
  type MapCityView,
  type PlayerId,
  type SimEvent,
  type SimEventKind,
  type SimId,
  type StrikeTypeId,
} from '@taxwar/net'
import type { LatLonOut, MatchView, StrikeFlightOut } from '../match/contracts'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../render/geo/globeSpace'
import type { EffectIdFor } from './effects'
import type { FlightSystem } from './FlightSystem'
import type { WarGeography } from './geography'
import {
  placePath,
  placePoint,
  type FlightPlacement,
  type PathPlacement,
  type PlacementByKind,
  type PlacementKind,
  type PointPlacement,
} from './placement'
import type { LastKnownPositions } from './positions'

/** Angular size of a city node / a unit hologram (radians): the extent of effects about them. */
const CITY_EXTENT_RAD = 0.02
const UNIT_EXTENT_RAD = 0.01
/** Smallest strike-area extent drawn, so a pinpoint blast still reads at theatre zoom. */
const MIN_AREA_EXTENT_RAD = 0.008
const NO_AMOUNT = 0
const INSTANT = 0

/** Plays registry effects at a placement; the id lists are typed by the placement kind their primitives use. */
export interface EffectPlayer {
  point(ids: readonly EffectIdFor<'point'>[], placement: PointPlacement): void
  path(ids: readonly EffectIdFor<'path'>[], placement: PathPlacement): void
  flight(ids: readonly EffectIdFor<'flight'>[], placement: FlightPlacement): void
}

export interface BindingWorld {
  readonly match: MatchView
  readonly geography: WarGeography
  readonly positions: LastKnownPositions
  readonly flights: FlightSystem
}

type EventOf<K extends SimEventKind> = Extract<SimEvent, { readonly kind: K }>

interface Binding<K extends SimEventKind, P extends PlacementKind> {
  readonly placement: P
  /** Effects this event plays through this binding (empty = nothing to place). */
  readonly effects: (event: EventOf<K>) => readonly EffectIdFor<P>[]
  /**
   * Fills `out` and plays `ids` once per anchor the viewer knows: zero times when the position is unknown (a unit
   * killed out of sight of any cache), several for multi-anchor events (a sanctioned player's cities).
   */
  readonly play: (event: EventOf<K>, world: BindingWorld, ids: readonly EffectIdFor<P>[], out: PlacementByKind[P], fx: EffectPlayer) => void
}

type AnyBinding<K extends SimEventKind> = Binding<K, 'point'> | Binding<K, 'path'> | Binding<K, 'flight'>

/** The erased shape the dispatcher calls; EVENT_BINDINGS' mapped type guarantees each entry matches its kind. */
export interface ErasedBinding {
  readonly placement: PlacementKind
  readonly effects: (event: SimEvent) => readonly string[]
  readonly play: (event: SimEvent, world: BindingWorld, ids: readonly string[], out: PlacementByKind[PlacementKind], fx: EffectPlayer) => void
}

// ------------------------------------------------------------------------------------------------ strikes

/** Per strike type: what plays at launch (origin city, launch path, flight, target point) and at each impact. */
interface StrikeVfx {
  readonly atOrigin: readonly EffectIdFor<'point'>[]
  readonly launch: readonly EffectIdFor<'path'>[]
  readonly flight: readonly EffectIdFor<'flight'>[]
  readonly atTarget: readonly EffectIdFor<'point'>[]
  readonly impact: readonly EffectIdFor<'point'>[]
}

const MISSILE_LAUNCH = { atOrigin: ['launchGlitchRing'], launch: ['launchStreak'], flight: ['strikeFlight'], atTarget: [] } as const satisfies Omit<StrikeVfx, 'impact'>

const STRIKE_VFX: Readonly<Record<StrikeKey, StrikeVfx>> = {
  precision_strike: { ...MISSILE_LAUNCH, impact: ['impactCore', 'impactShockwave', 'impactSparks', 'impactStatic'] },
  shock_and_awe: { ...MISSILE_LAUNCH, impact: ['impactCore', 'impactShockwave', 'impactShockwaveEcho', 'impactSparksHeavy', 'impactStatic'] },
  // Sanctions play through sanctionsApplied (INVOICE rain on the target's cities).
  sanctions: { atOrigin: [], launch: [], flight: [], atTarget: [], impact: [] },
  leaflet_drop: { atOrigin: [], launch: [], flight: [], atTarget: ['leafletFlutter'], impact: [] },
}

type StrikeEffectOf<K extends StrikeEffect['kind']> = Extract<StrikeEffect, { readonly kind: K }>

/** Content radius of a strike's area in mu (null = not aimed at an area). */
const AREA_OF_EFFECT: { readonly [K in StrikeEffect['kind']]: (effect: StrikeEffectOf<K>) => number | null } = {
  missile: (effect) => effect.radius,
  sanctions: () => null,
  leaflets: (effect) => effect.radius,
}

/** Salvos a flying strike resolves (0 = it never flies). */
const SALVOS_OF_EFFECT: { readonly [K in StrikeEffect['kind']]: (effect: StrikeEffectOf<K>) => number } = {
  missile: (effect) => effect.salvos,
  sanctions: () => 0,
  leaflets: () => 0,
}

function strikeVfx(strikeType: StrikeTypeId): StrikeVfx {
  return STRIKE_VFX[keyOf(STRIKE_KEYS, strikeType)]
}

function strikeEffect(strikeType: StrikeTypeId): StrikeEffect {
  return STRIKES[keyOf(STRIKE_KEYS, strikeType)].effect
}

/** Angular radius (radians) of the strike's content area of effect on this war map (at least MIN_AREA_EXTENT_RAD). */
function strikeExtent(world: BindingWorld, strikeType: StrikeTypeId): number {
  const effect = strikeEffect(strikeType)
  const radiusMu = (AREA_OF_EFFECT[effect.kind] as (effect: StrikeEffect) => number | null)(effect)
  if (radiusMu === null) throw new InvariantViolation(`strike type ${strikeType} has area effects bound but no area`)
  return Math.max(MIN_AREA_EXTENT_RAD, radiusMu / world.geography.setup.muPerRadian)
}

function strikeSalvos(strikeType: StrikeTypeId): number {
  const effect = strikeEffect(strikeType)
  return (SALVOS_OF_EFFECT[effect.kind] as (effect: StrikeEffect) => number)(effect)
}

/**
 * Fills `out` for a strike first seen in flight (no strikeLaunched event reached this viewer, e.g. an enemy launch
 * outside its sight) and returns the strike type's flight effects.
 */
export function placeSightedFlight(strike: SimId, flight: StrikeFlightOut, out: FlightPlacement): readonly EffectIdFor<'flight'>[] {
  out.owner = flight.owner
  out.strike = strike
  latLonToVector3(flight.originLat, flight.originLon, 1, out.origin)
  latLonToVector3(flight.targetLat, flight.targetLon, 1, out.target)
  out.salvos = strikeSalvos(flight.strikeType)
  return strikeVfx(flight.strikeType).flight
}

// ------------------------------------------------------------------------------------------------ bindings

const found: LatLonOut = { lat: 0, lon: 0 }
const other: LatLonOut = { lat: 0, lon: 0 }

/**
 * Render radius of a strike for its interception: on its arc when this viewer follows its flight; otherwise the arc
 * (whose target only the launcher knows) is unknown and the interception plays at the ground point below it.
 */
function interceptAltitude(world: BindingWorld, strike: SimId): number {
  const altitude = world.flights.altitudeOf(strike)
  return altitude === null ? GLOBE_ALTITUDE.network : altitude
}

/**
 * Ownership sweep (ART-DIRECTION §7 City captured): the owner's colour sweeps around the city and along its edges.
 * Shared by captures and restorations (a fallen city re-raised by its own owner).
 */
function ownershipSweep<K extends SimEventKind>(cityOf: (event: EventOf<K>) => SimId, ownerOf: (event: EventOf<K>) => PlayerId): readonly AnyBinding<K>[] {
  const ring: Binding<K, 'point'> = {
    placement: 'point',
    effects: () => ['captureSweepRing'],
    play: (event, world, ids, out, fx) => {
      const city = world.geography.city(cityOf(event))
      fx.point(ids, placePoint(out, ownerOf(event), city.lat, city.lon, CITY_EXTENT_RAD, NO_AMOUNT, INSTANT))
    },
  }
  const edges: Binding<K, 'path'> = {
    placement: 'path',
    effects: () => ['captureEdgeSweep'],
    play: (event, world, ids, out, fx) => {
      const city = world.geography.city(cityOf(event))
      const owner = ownerOf(event)
      const neighbours = world.geography.neighbours(city.index)
      for (let i = 0; i < neighbours.length; i++) {
        const neighbour = world.geography.cityAt(neighbours[i] as CityIndex)
        fx.path(ids, placePath(out, owner, city.lat, city.lon, neighbour.lat, neighbour.lon))
      }
    },
  }
  return [ring, edges]
}

export const EVENT_BINDINGS: { readonly [K in SimEventKind]: readonly AnyBinding<K>[] } = {
  unitDeployed: [
    {
      placement: 'point',
      effects: () => ['deployMaterialize', 'deployFloater'],
      play: (event, world, ids, out, fx) => {
        const city = world.geography.city(event.city)
        fx.point(ids, placePoint(out, event.owner, city.lat, city.lon, CITY_EXTENT_RAD, event.cost, INSTANT))
      },
    },
  ],
  // A carrier's fighter materializes at the carrier (it spawns there); no purchase, so no floater.
  unitSpawned: [
    {
      placement: 'point',
      effects: () => ['deployMaterialize'],
      play: (event, world, ids, out, fx) => {
        if (!world.positions.locate(event.parent, found)) return
        fx.point(ids, placePoint(out, event.owner, found.lat, found.lon, UNIT_EXTENT_RAD, NO_AMOUNT, INSTANT))
      },
    },
  ],
  unitKilled: [
    {
      placement: 'point',
      effects: () => ['unitShatter', 'signalLostLabel'],
      play: (event, world, ids, out, fx) => {
        if (!world.positions.locate(event.unit, found)) return
        fx.point(ids, placePoint(out, event.owner, found.lat, found.lon, UNIT_EXTENT_RAD, NO_AMOUNT, INSTANT))
      },
    },
  ],
  buildingBuilt: [],
  buildingDestroyed: [],
  cityFallen: [],
  cityRestored: ownershipSweep<'cityRestored'>(
    (event) => event.city,
    (event) => event.owner,
  ),
  cityCaptured: ownershipSweep<'cityCaptured'>(
    (event) => event.city,
    (event) => event.newOwner,
  ),
  strikeLaunched: [
    {
      placement: 'point',
      effects: (event) => strikeVfx(event.strikeType).atOrigin,
      play: (event, world, ids, out, fx) => {
        const origin = world.geography.strikeOrigin(event.owner, event.site)
        fx.point(ids, placePoint(out, event.owner, origin.lat, origin.lon, CITY_EXTENT_RAD, event.cost, INSTANT))
      },
    },
    {
      placement: 'path',
      effects: (event) => strikeVfx(event.strikeType).launch,
      play: (event, world, ids, out, fx) => {
        const origin = world.geography.strikeOrigin(event.owner, event.site)
        fx.path(ids, placePath(out, event.owner, origin.lat, origin.lon, event.lat, event.lon))
      },
    },
    {
      placement: 'flight',
      effects: (event) => strikeVfx(event.strikeType).flight,
      play: (event, world, ids, out, fx) => {
        if (event.strike === NO_SIM_ID) throw new InvariantViolation(`strike type ${event.strikeType} has flight effects bound but no flight entity`)
        const origin = world.geography.strikeOrigin(event.owner, event.site)
        out.owner = event.owner
        out.strike = event.strike
        latLonToVector3(origin.lat, origin.lon, 1, out.origin)
        latLonToVector3(event.lat, event.lon, 1, out.target)
        out.salvos = strikeSalvos(event.strikeType)
        fx.flight(ids, out)
      },
    },
    {
      placement: 'point',
      effects: (event) => strikeVfx(event.strikeType).atTarget,
      play: (event, world, ids, out, fx) => {
        fx.point(ids, placePoint(out, event.owner, event.lat, event.lon, strikeExtent(world, event.strikeType), event.cost, INSTANT))
      },
    },
  ],
  strikeIntercepted: [
    {
      placement: 'point',
      effects: () => ['interceptPixelSort', 'interceptSparks'],
      play: (event, world, ids, out, fx) => {
        if (!world.positions.locate(event.strike, found)) return
        // Interception effects use fixed hues: the strike's owner is not always known to this viewer.
        placePoint(out, NEUTRAL_OWNER, found.lat, found.lon, UNIT_EXTENT_RAD, NO_AMOUNT, INSTANT)
        out.altitude = interceptAltitude(world, event.strike)
        fx.point(ids, out)
      },
    },
    {
      placement: 'path',
      effects: () => ['interceptBeam'],
      play: (event, world, ids, out, fx) => {
        if (!world.positions.locate(event.interceptor, other) || !world.positions.locate(event.strike, found)) return
        placePath(out, NEUTRAL_OWNER, other.lat, other.lon, found.lat, found.lon)
        out.toAltitude = interceptAltitude(world, event.strike)
        fx.path(ids, out)
      },
    },
  ],
  strikeImpact: [
    {
      placement: 'point',
      effects: (event) => strikeVfx(event.strikeType).impact,
      play: (event, world, ids, out, fx) => {
        // Impact effects use fixed hues: an enemy strike launched outside this viewer's sight has no known owner.
        fx.point(ids, placePoint(out, NEUTRAL_OWNER, event.lat, event.lon, strikeExtent(world, event.strikeType), NO_AMOUNT, INSTANT))
      },
    },
  ],
  sanctionsApplied: [
    {
      placement: 'point',
      effects: () => ['invoiceRain'],
      play: (event, world, ids, out, fx) => {
        const seconds = (event.untilTick - event.tick) / SIM_HZ
        const cities = world.geography.setup.cities
        for (let i = 0; i < cities.length; i++) {
          const city = cities[i] as MapCityView
          if (world.match.cityState(city.index).owner !== event.target) continue
          fx.point(ids, placePoint(out, event.by, city.lat, city.lon, CITY_EXTENT_RAD, NO_AMOUNT, seconds))
        }
      },
    },
  ],
  taxChanged: [],
  approvalThreshold: [],
  phaseChanged: [],
  commandRejected: [],
  outcomeDecided: [],
}

export const EVENT_BOOKKEEPING: { readonly [K in SimEventKind]?: (event: EventOf<K>, world: BindingWorld, now: number) => void } = {
  unitDeployed: (event, world) => world.positions.track(event.unit),
  unitSpawned: (event, world) => world.positions.track(event.unit),
  unitKilled: (event, world) => world.positions.forget(event.unit),
  strikeIntercepted: (event, world, now) => world.flights.resolveSalvo(event.strike, now),
  strikeImpact: (event, world, now) => world.flights.resolveSalvo(event.strike, now),
}

export function bindingsOf(kind: SimEventKind): readonly ErasedBinding[] {
  return EVENT_BINDINGS[kind] as unknown as readonly ErasedBinding[]
}

export function bookkeepingOf(kind: SimEventKind): ((event: SimEvent, world: BindingWorld, now: number) => void) | undefined {
  return EVENT_BOOKKEEPING[kind] as ((event: SimEvent, world: BindingWorld, now: number) => void) | undefined
}
